import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye, Plus } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/api";

export default function ProductCard({ product, index = 0 }) {
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWished, setQuickView } = useStore();
  const wished = isWished(product.id);

  return (
    <motion.div
      layout
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group bg-surface border border-line/70 p-2.5 sm:p-4 transition-all duration-500 hover:shadow-[0_30px_60px_-25px_rgba(42,36,33,0.3)] hover:-translate-y-1.5 hover:border-mocha"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-beige mb-3 sm:mb-5 rounded-[2px]">
        <img
          src={product.image}
          alt={product.name}
          onClick={() => navigate(`/parfum/${product.id}`)}
          className="w-full h-full object-cover cursor-pointer transition-transform duration-[900ms] ease-out [@media(hover:hover)]:group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/15 to-transparent opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

        {product.discount_pct ? (
          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-ink text-[#EBC98A] text-[10px] sm:text-[11px] tracking-[0.12em] px-2.5 py-1 sm:px-3 sm:py-1.5 font-bold rounded-full shadow-md">
            −{product.discount_pct}%
          </span>
        ) : null}

        <button
          data-testid={`wishlist-btn-${product.id}`}
          onClick={() => toggleWishlist(product)}
          aria-label="Favorite"
          className={`absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-cream/85 backdrop-blur rounded-full shadow-sm transition-all duration-300 hover:scale-110 ${wished ? "text-[#C1362F]" : "text-ink"}`}
        >
          <Heart size={16} strokeWidth={1.5} fill={wished ? "#C1362F" : "none"} />
        </button>

        <div className="hidden md:flex absolute inset-x-3 bottom-3 gap-2 translate-y-3 opacity-0 [@media(hover:hover)]:group-hover:translate-y-0 [@media(hover:hover)]:group-hover:opacity-100 transition-all duration-500">
          <button
            data-testid={`quickview-btn-${product.id}`}
            onClick={() => setQuickView(product)}
            className="flex-1 flex items-center justify-center gap-2 bg-cream/95 backdrop-blur text-ink text-[11px] uppercase tracking-widest py-2.5 rounded-full hover:bg-cream transition-colors"
          >
            <Eye size={14} strokeWidth={1.5} /> Vizualizare
          </button>
        </div>
      </div>

      <div className="px-0.5 sm:px-1.5 pb-1">
        <p className="eyebrow text-[0.55rem] sm:text-[0.6rem] text-mocha-dark mb-1 sm:mb-1.5">{product.brand} · {product.category}</p>
        <h3
          onClick={() => navigate(`/parfum/${product.id}`)}
          className="font-serif text-xl sm:text-[1.6rem] text-ink leading-snug cursor-pointer hover:text-mocha-dark transition-colors"
        >
          {product.name}
        </h3>

        <div className="mt-2 sm:mt-3">
          {product.stock <= 0 ? (
            <span className="text-[11px] uppercase tracking-widest text-[#C1362F] font-semibold">Sold Out</span>
          ) : product.stock <= 3 ? (
            <span className="text-[11px] uppercase tracking-widest text-mocha-dark font-semibold">Stoc redus · {product.stock} buc.</span>
          ) : (
            <span className="text-[11px] uppercase tracking-widest text-ink-muted">În stoc</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-1.5 sm:mt-2 gap-2 sm:gap-3">
          <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
            <span className="text-ink text-base sm:text-lg font-semibold">{formatPrice(product.price)}</span>
            {product.original_price ? (
              <span className="text-ink-muted text-xs sm:text-sm line-through">{formatPrice(product.original_price)}</span>
            ) : null}
          </div>
          {product.stock <= 0 ? (
            <span data-testid={`sold-out-${product.id}`} className="shrink-0 px-3 h-9 flex items-center bg-ink/10 text-ink-muted text-[10px] uppercase tracking-widest rounded-full cursor-not-allowed">Sold Out</span>
          ) : (
            <button
              data-testid={`add-to-cart-btn-${product.id}`}
              onClick={() => addToCart(product)}
              aria-label="Adaugă în coș"
              className="shrink-0 w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center bg-ink text-cream rounded-full hover:bg-mocha-dark transition-all duration-300 hover:rotate-90"
            >
              <Plus size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
